<template>
  <node-view-wrapper as="div" class="link-button-block not-prose" contenteditable="false">
    <block-controls :editor="editor" :get-pos="getPos" :delete-node="deleteNode" :label="$t('products.block_link_button')" />

    <!-- Preview: same inline styles as the saved HTML (see linkButtonStyles.js) -->
    <div :style="styles.wrapper">
      <a v-if="validUrl" :href="validUrl" target="_blank" rel="noopener noreferrer" :style="buttonStyle" @click.prevent>{{ node.attrs.text }}</a>
      <p v-else :id="fieldId('no-link')" role="status" class="link-button-warning">{{ $t('products.link_button_no_link') }}</p>
    </div>

    <!-- Form -->
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 mt-3">
      <div>
        <label :for="fieldId('text')" class="link-button-label">{{ $t('products.link_button_text') }}</label>
        <input :id="fieldId('text')" type="text" :value="node.attrs.text" class="link-button-input"
               @input="update('text', $event.target.value)" />
      </div>
      <div>
        <label :for="fieldId('url')" class="link-button-label">{{ $t('products.link_button_url') }}</label>
        <input :id="fieldId('url')" type="url" :value="urlDraft" class="link-button-input"
               placeholder="https://..."
               :aria-invalid="urlError ? 'true' : 'false'"
               :aria-describedby="urlDescribedBy"
               @input="onUrlInput($event.target.value)" @change="commitUrl" />
        <p v-if="urlError" :id="fieldId('url-error')" class="mt-1 text-xs text-red-700">
          {{ $t('products.link_button_url_invalid') }}
        </p>
      </div>
      <div>
        <label :for="fieldId('color')" class="link-button-label">{{ $t('products.link_button_color') }}</label>
        <input :id="fieldId('color')" type="color" :value="buttonColor"
               class="mt-1 h-9 w-16 cursor-pointer rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-bpblue"
               @input="update('color', normalizeHexColor($event.target.value) || defaultColor)" />
      </div>
      <div>
        <label :for="fieldId('variant')" class="link-button-label">{{ $t('products.link_button_variant') }}</label>
        <select :id="fieldId('variant')" :value="variant" class="link-button-input"
                @change="update('variant', normalizeLinkButtonVariant($event.target.value))">
          <option value="solid">{{ $t('products.link_button_variant_solid') }}</option>
          <option value="outline">{{ $t('products.link_button_variant_outline') }}</option>
        </select>
      </div>
    </div>
  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'
import BlockControls from './BlockControls.vue'
import { normalizeHexColor } from './colorAttributes'
import { safeLinkUrl } from './linkAttributes'
import { DEFAULT_LINK_BUTTON_COLOR, LINK_BUTTON_STYLES, linkButtonStyle, normalizeLinkButtonVariant } from './linkButtonStyles'

export default {
  name: 'LinkButtonNodeView',

  components: {
    NodeViewWrapper,
    BlockControls,
  },

  props: nodeViewProps,

  data() {
    return {
      // The link is only stored once it's valid; meanwhile the field keeps what's typed.
      urlDraft: this.node.attrs.url,
      urlError: false,
      styles: LINK_BUTTON_STYLES,
      defaultColor: DEFAULT_LINK_BUTTON_COLOR,
    }
  },

  computed: {
    buttonColor() {
      return normalizeHexColor(this.node.attrs.color) || DEFAULT_LINK_BUTTON_COLOR
    },
    variant() {
      return normalizeLinkButtonVariant(this.node.attrs.variant)
    },
    buttonStyle() {
      return linkButtonStyle(this.buttonColor, this.variant)
    },
    validUrl() {
      return safeLinkUrl(this.node.attrs.url)
    },
    // The link field points to the visible warnings: the format error and, without a valid link, the preview notice.
    urlDescribedBy() {
      const ids = []
      if (this.urlError) ids.push(this.fieldId('url-error'))
      if (!this.validUrl) ids.push(this.fieldId('no-link'))
      return ids.join(' ') || null
    },
  },

  watch: {
    // Keep the field in sync when the link changes from outside (undo/redo).
    'node.attrs.url'(value) {
      this.urlDraft = value
      this.urlError = false
    },
  },

  methods: {
    normalizeHexColor,
    normalizeLinkButtonVariant,
    fieldId(name) {
      return `link-button-${this.$.uid}-${name}`
    },
    update(name, value) {
      this.updateAttributes({ [name]: value })
    },
    onUrlInput(value) {
      this.urlDraft = value
      this.urlError = false
    },
    commitUrl() {
      const value = this.urlDraft.trim()
      const url = safeLinkUrl(value)
      if (value && !url) {
        this.urlError = true
        return
      }
      this.update('url', url || '')
    },
  },
}
</script>

<style scoped>
.link-button-block {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #ffffff;
}

.link-button-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
}

.link-button-input {
  margin-top: 0.25rem;
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.link-button-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3297cc;
}

/* Takes the button's place in the preview so a block without link can't go unnoticed.
   #92400e on #fffbeb is ~6.8:1. */
.link-button-warning {
  margin: 0;
  padding: 12px 16px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #92400e;
  background: #fffbeb;
  border: 1px dashed #f59e0b;
  border-radius: 10px;
}
</style>
